import { memo, lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { arrayMove } from 'react-sortable-hoc';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Typography,
  Divider,
  Stack,
  Button,
  TextField,
  Switch,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { set } from 'lodash';
import { CONTEXT_TYPES } from 'constants/menu';
import { SET_CHANGE_PRICE_V2 } from 'redux/actions/types';
import Preloader from 'components/Preloader';
import { priceTransform } from 'helpers/formPrice';
import {
  onWheelPreventChangeNumberField,
  buildProviderPriceOverrides,
  mergeProviderPriceOverrides,
  cloneProviderPriceOverrides,
  sizeOverrideMatchesSize,
  isUnboundSizeOverride,
} from 'utils/menu';
import { useTranslation } from 'react-i18next';
import LocalizationPopover from 'components/LocalizationPopover';
import useProviderConfig from 'hooks/useProviderConfig';
import { validationModifierCustomersCan } from 'utils/globalUtils';
import { NAME_ENUMS } from 'constants/products';
import MinAndMaxOptions from 'components/MenuNew/FormView/ModifierFormGroup/MinAndMaxOptions';
import { useLocalizationPopover } from 'components/LocalizationPopover/hooks/useLocalizationPopover';
import useMinAndMaxOptions from 'components/MenuNew/FormView/ModifierFormGroup/hooks/useMinAndMaxOptions';
import useQuery from 'hooks/useQuery';
import {
  getAvailabilityResetAt,
  toSoldOutPayload,
} from 'constants/availabilityStatus';
import { patchModifierOptionSoldOut } from 'redux/actions/Menu/modifiers';
import moment from 'moment-timezone';

// import SortableContainerList from './SortableContainerList';
const SortableContainerList = lazy(() => import('./SortableContainerList'));

const hasProviderCustomizations = (overrides) =>
  Boolean(
    overrides?.find(
      (item) =>
        item.context_type === CONTEXT_TYPES.provider &&
        item.context_value !== 'main'
    )
  );

const isModifierSizeOverride = (item) =>
  item?.context_type === CONTEXT_TYPES.modifierItem;

const hasModifierSizePrices = (overrides) =>
  Boolean(overrides?.some(isModifierSizeOverride));

const sizeOverrideKey = (item) =>
  item?.context_value ||
  String(item?.name || '')
    .trim()
    .toLowerCase();

const dedupeSizePriceOverrides = (overrides = []) => {
  const preferredIndexBySize = new Map();
  overrides.forEach((item, index) => {
    if (!isModifierSizeOverride(item)) {
      return;
    }
    const key = sizeOverrideKey(item);
    if (!key) {
      return;
    }
    const current = preferredIndexBySize.get(key);
    if (
      current == null ||
      (Number(item.price) && !Number(overrides[current]?.price))
    ) {
      preferredIndexBySize.set(key, index);
    }
  });
  const keep = new Set(preferredIndexBySize.values());
  return overrides.filter((item, index) => {
    if (!isModifierSizeOverride(item)) {
      return true;
    }
    const key = sizeOverrideKey(item);
    if (!key) {
      return true;
    }
    return keep.has(index);
  });
};

const overridesStructureChanged = (current = [], next = []) =>
  current.length !== next.length ||
  next.some((item, index) => {
    const existing = current[index];
    return (
      !existing ||
      existing.context_value !== item.context_value ||
      existing.context_type !== item.context_type
    );
  });

const lockProviderOverrides = (overrides = []) =>
  overrides.map((item) =>
    item.context_type === CONTEXT_TYPES.provider
      ? { ...item, locked: true }
      : item
  );

const toComparablePrice = (value) => {
  if (value === '' || value === null || value === undefined) {
    return value;
  }
  return Number(Number(value).toFixed(2));
};

const cloneModifierList = (list = []) =>
  (list || []).map((item) => ({
    ...item,
    priceOverride: cloneProviderPriceOverrides(item.priceOverride),
  }));

const SIZE_PRICE_PATH =
  /^list\[(\d+)\]\.priceOverride\[(\d+)\]\.price$/;

const ModifierGroupForm = ({
  mode,
  // changeIsOnePrice,
  currentProduct,
  isInOverview,
  isInModifierGroupPage,
  formState,
  formErrors,
  updateFormState,
  updateFormErrors,
  handleErrors,
  setIsDirty,
  setIsChanged,
  setChanged,
  savedListVersion = 0,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { providers } = useProviderConfig();
  const originalListRef = useRef(null);
  const { currentBusiness } = useSelector(({ business }) => business);
  const {
    open,
    handleClose,
    handleClickPopover,
    languageLimit,
    currentSelectedField,
    currentSelectedLabel,
    currentSelectedValues,
    inputRef,
    textAreaRef,
    internalRef,
  } = useLocalizationPopover();
  const {
    isOnePrice,
    products,
    list,
    name,
    title,
    description,
    min,
    max,
    free,
    _id,
    minTotalNumberOfModifierItems,
    maxTotalNumberOfModifierItems,
    minAnySingleModifierItems,
    maxAnySingleModifierItems,
    canSelectMultipleOptions,
  } = formState;
  const isEdit = mode === 'edit';
  const currentProductIsUber =
    currentProduct?.connectedProviders?.[providers?.ubereats?.srv] || false;
  const { menu, loading, changeBasePriceData } = useSelector(
    ({ menu }) => menu
  );
  const sizeTypeModifiers = currentProduct?.sizeTypeModifiers;
  let connectedProviders = menu.connectedProviders;
  if (!isInModifierGroupPage) {
    connectedProviders = currentProduct?.connectedProviders;
  }
  const {
    handleChangeMultipleOptions,
    handleChangeFieldMaxTotalNumberOfModifierItems,
    handleChangeFieldMinTotalNumberOfModifierItems,
  } = useMinAndMaxOptions(
    setIsDirty,
    setIsChanged,
    formState,
    updateFormState,
    formErrors,
    updateFormErrors,
    list,
    max
  );
  const checkUber =
    products?.some(
      (item) => item.connectedProviders?.[providers.ubereats.srv]
    ) || currentProductIsUber;
  const labelProps = {
    shrink: true,
  };
  const query = useQuery();
  const entityType = query.get('entityType');
  const connectedProviderKey = Object.entries(connectedProviders || {})
    .filter(([, enabled]) => enabled)
    .map(([key]) => key)
    .sort()
    .join(',');
  const hasSizePrices = list?.some((item) =>
    hasModifierSizePrices(item.priceOverride)
  );
  /**
   * Sizes this group prices by: the product's sizes when the group is opened
   * from an item, otherwise the ones its own options already carry. Options
   * saved before the group became size-priced hold none of them, and without
   * this the group renders size inputs on some options only.
   */
  const resolveSizeSources = useCallback(
    (optionList) => {
      const productSizes = sizeTypeModifiers?.[0]?.list || [];
      const sources = productSizes.length
        ? productSizes
        : (optionList || [])
            .flatMap((option) =>
              (option?.priceOverride || []).filter(isModifierSizeOverride)
            )
            // A size row points at its size through context_value. Its own
            // `_id` belongs to the row, and copying that onto a new option
            // gives the option a size the item cannot resolve.
            .map((override) => ({
              _id: override.context_value,
              name: override.name,
            }));
      const seen = new Set();
      return sources.filter((size) => {
        const key = size?._id || sizeOverrideKey(size);
        if (!key || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    },
    [sizeTypeModifiers]
  );
  // Standalone modifier groups have no product sizes. If the API omits
  // isOnePrice or the option only has provider overrides, keep the one-price UI
  // so provider prices remain editable.
  const effectiveIsOnePrice = isInModifierGroupPage
    ? isOnePrice !== false && !hasSizePrices
    : Boolean(isOnePrice);

  useEffect(() => {
    if (mode === 'create' && entityType === 'modifier') {
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [mode, inputRef]);

  useEffect(() => {
    if (checkUber && list?.length) {
      const indexOf = list.findIndex((item) => item.price > 374);
      if (indexOf !== -1 && !menu?.disablePriceLimitCheck) {
        updateFormErrors({
          ...set(formErrors, `list[${indexOf}].price`, {
            error: true,
            message: t('price_$375.00_for_ubereats_'),
          }),
        });
      }
    }
  }, [checkUber, list, formState]);

  useEffect(() => {
    if (!formState.list?.length) {
      return;
    }
    // Wait for GET data in edit mode so the initial empty option is not
    // merged into $0 provider prices and then written over the saved values.
    if (mode === 'edit' && !formState._id) {
      return;
    }
    const providerOverrides = buildProviderPriceOverrides(
      connectedProviders,
      CONTEXT_TYPES
    );
    let changed = false;
    const formStateCopy = {
      ...formState,
      list: formState.list.map((listItem) => ({
        ...listItem,
        priceOverride: [...(listItem.priceOverride || [])],
      })),
    };

    const sizeSources = resolveSizeSources(formStateCopy.list);

    formStateCopy.list.forEach((listItem) => {
      const hasModItems = hasModifierSizePrices(listItem.priceOverride);

      if (!hasModItems && (effectiveIsOnePrice || !sizeSources.length)) {
        const merged = mergeProviderPriceOverrides(
          listItem.priceOverride,
          connectedProviders,
          CONTEXT_TYPES,
          listItem.price
        );
        if (overridesStructureChanged(listItem.priceOverride, merged)) {
          listItem.priceOverride = merged;
          changed = true;
        }
        return;
      }

      const priceOverride = dedupeSizePriceOverrides(
        listItem.priceOverride || []
      );
      if (priceOverride.length !== (listItem.priceOverride || []).length) {
        changed = true;
      }
      // Every option of a size-priced group needs a row per size, or it shows
      // no size inputs at all. An unbound row is claimed by name so standalone
      // groups do not gain a duplicate; a row already bound to another item's
      // size is not, or this item would inherit that item's price.
      sizeSources.forEach((currentItem) => {
        const found = priceOverride.find(
          (item) =>
            isModifierSizeOverride(item) &&
            sizeOverrideMatchesSize(item, currentItem)
        );
        if (!found) {
          priceOverride.push({
            context_type: CONTEXT_TYPES.modifierItem,
            context_value: currentItem?._id || currentItem?.context_value,
            name: currentItem.name,
            // Seed with the option's own price so an option that was flat
            // priced until now keeps costing the same.
            price: listItem.price ?? 0,
            priceOverride: cloneProviderPriceOverrides(providerOverrides),
          });
          changed = true;
        } else {
          if (isModifierSizeOverride(found) && !found?.name) {
            found.name = currentItem?.name || '';
            changed = true;
          }
          const sizeId = currentItem?._id || currentItem?.context_value;
          // Claimed by name: bind the row to this item's size so it is
          // recognised without the fallback, and so another item that happens
          // to use the same size name no longer picks it up.
          if (sizeId && isUnboundSizeOverride(found)) {
            found.context_value = sizeId;
            changed = true;
          }
          const mergedNested = mergeProviderPriceOverrides(
            found.priceOverride,
            connectedProviders,
            CONTEXT_TYPES,
            found.price ?? listItem.price
          );
          if (overridesStructureChanged(found.priceOverride, mergedNested)) {
            found.priceOverride = mergedNested;
            changed = true;
          }
        }
      });

      priceOverride.forEach((item) => {
        if (!isModifierSizeOverride(item)) {
          return;
        }
        const mergedNested = mergeProviderPriceOverrides(
          item.priceOverride,
          connectedProviders,
          CONTEXT_TYPES,
          item.price ?? listItem.price
        );
        if (overridesStructureChanged(item.priceOverride, mergedNested)) {
          item.priceOverride = mergedNested;
          changed = true;
        }
      });

      listItem.priceOverride = priceOverride;
    });

    if (changed) {
      updateFormState(formStateCopy);
      if (mode === 'edit' && formState._id) {
        originalListRef.current = JSON.parse(
          JSON.stringify(formStateCopy.list)
        );
      }
    }
  }, [
    connectedProviderKey,
    currentProduct?.isOnePrice,
    effectiveIsOnePrice,
    formState._id,
    formState.list?.length,
    isInModifierGroupPage,
    mode,
    resolveSizeSources,
    sizeTypeModifiers?.[0]?.list,
  ]);

  useEffect(() => {
    originalListRef.current = null;
  }, [formState._id, mode, savedListVersion]);

  useEffect(() => {
    if (
      mode === 'edit' &&
      formState._id &&
      !originalListRef.current &&
      formState.list?.length
    ) {
      originalListRef.current = JSON.parse(JSON.stringify(formState.list));
    }
  }, [mode, formState._id, formState.list, savedListVersion]);

  const handleAddItem = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (handleErrors(formState, true)) {
      return;
    }
    if (effectiveIsOnePrice) {
      const withNewList = {
        ...formState,
        list: [
          {
            name: '',
            price: 0,
            priceOverride: buildProviderPriceOverrides(
              connectedProviders,
              CONTEXT_TYPES
            ),
            optionSort: 0,
            connectedProviders: connectedProviders,
            soldOut: { status: 'available', soldOutDate: null },
          },
          ...list,
        ],
      };
      updateFormState(withNewList);
    } else {
      const newPriceOverride = buildProviderPriceOverrides(
        connectedProviders,
        CONTEXT_TYPES
      );
      const priceOverride = [];
      const sizeSources = resolveSizeSources(list);
      sizeSources.forEach((currentItem) => {
        priceOverride.push({
          context_type: CONTEXT_TYPES.modifierItem,
          context_value: currentItem?._id || currentItem?.context_value,
          name: currentItem.name,
          price: 0,
          priceOverride: cloneProviderPriceOverrides(newPriceOverride),
        });
      });
      const withNewList = {
        ...formState,
        list: [
          {
            name: '',
            price: 0,
            priceOverride,
            optionSort: 0,
            connectedProviders: connectedProviders,
            soldOut: { status: 'available', soldOutDate: null },
          },
          ...list,
        ],
      };
      updateFormState(withNewList);
    }
    if (setIsDirty) {
      setIsDirty(true);
    }
    if (setIsChanged) {
      setIsChanged(true);
    }
  };

  const handleOptionAvailabilityChange = (itemIndex, availabilityStatus) => {
    const soldOut = toSoldOutPayload(
      availabilityStatus,
      currentBusiness?.timezone?.timeZoneId,
      moment
    );
    const previousList = formState.list;
    const nextList = formState.list.map((opt, index) =>
      index === itemIndex
        ? {
            ...opt,
            soldOut,
            availability_status: availabilityStatus,
            availability_resets_at: getAvailabilityResetAt(
              availabilityStatus,
              currentBusiness?.timezone?.timeZoneId,
              moment
            ),
          }
        : opt
    );
    const nextState = { ...formState, list: nextList };
    updateFormState(nextState);

    const option = nextList[itemIndex];
    if (option?._id) {
      dispatch(
        patchModifierOptionSoldOut(option._id, { status: soldOut.status })
      ).catch(() => {
        updateFormState({ ...formState, list: previousList });
      });
    }
  };

  const handleDeleteItem = (index) => {
    const withNewList = {
      ...formState,
      list: formState?.list?.filter((item, listIndex) => listIndex !== index),
    };
    const newFormErrors = {
      ...formErrors,
      list: formErrors?.list?.filter((item, listIndex) => listIndex !== index),
    };
    updateFormErrors(newFormErrors);
    if (setIsDirty) {
      setIsDirty(true);
    }
    if (setIsChanged) {
      setIsChanged(true);
    }
    updateFormState(withNewList);

    if (withNewList?.max === null || withNewList?.max === '') {
      if (withNewList?.canSelectMultipleOptions) {
        const quantity =
          Number(withNewList?.list?.length) *
          Number(withNewList?.maxAnySingleModifierItems || 0);
        const newData = set(
          withNewList,
          'maxTotalNumberOfModifierItems',
          quantity
        );
        updateFormState({ ...newData });
      }
    }
  };

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const sortedList = arrayMove(formState.list, oldIndex, newIndex);
      updateFormState({ ...formState, list: sortedList });
      if (setIsDirty) {
        setIsDirty(true);
      }
      if (setIsChanged) {
        setIsChanged(true);
      }
    }
  };

  const handleChangeIsOnePrice = (e) => {
    const checked = e.target.checked;
    const formStateCopy = { ...formState, list: [...formState.list] };
    const newPriceOverride = buildProviderPriceOverrides(
      connectedProviders,
      CONTEXT_TYPES
    );

    formStateCopy.isOnePrice = !checked;
    if (!checked) {
      formStateCopy.list.forEach((listItem, index) => {
        formStateCopy.list[index].priceOverride =
          cloneProviderPriceOverrides(newPriceOverride);
      });
    } else {
      formStateCopy.list.forEach((listItem) => {
        const priceOverride = [];
        sizeTypeModifiers?.[0]?.list?.forEach((currentItem) => {
          const found = listItem.priceOverride?.find((item) => {
            return item.context_value === currentItem._id;
          });
          if (found) {
            priceOverride.push({
              ...found,
              priceOverride: found.priceOverride?.length
                ? cloneProviderPriceOverrides(found.priceOverride)
                : cloneProviderPriceOverrides(newPriceOverride),
            });
          } else {
            priceOverride.push({
              context_type: 'MOD_ITEM',
              context_value: currentItem?._id,
              name: currentItem.name,
              price: 0,
              priceOverride: cloneProviderPriceOverrides(newPriceOverride),
            });
          }
        });
        listItem.priceOverride = priceOverride;
      });
    }
    markModifierDirty();
    updateFormState(formStateCopy);
  };

  const validateNumber = (value, name) => {
    const reg = /^\d+$/;
    ///^\d+(\.\d{1,2})?$/
    let copyFormErrors = { ...formErrors };
    if (reg.test(value) && value > 0) {
      copyFormErrors = set(formErrors, name, { error: false, message: '' });
      updateFormErrors({ ...copyFormErrors });
      return true;
    } else {
      if (value) {
        copyFormErrors = set(formErrors, name, {
          error: true,
          message: 'Please enter a valid number',
        });
        updateFormErrors({ ...copyFormErrors });
        return false;
      }
    }
  };

  const handleChangeField = ({ currentTarget }) => {
    let { value, name } = currentTarget;
    if (setIsDirty) {
      setIsDirty(true);
    }
    if (name.includes('price')) {
      value = priceTransform(value);
    }
    let copyFormErrors = { ...formErrors };
    if (
      !value &&
      name !== 'max' &&
      name !== 'min' &&
      name !== 'description' &&
      name !== 'minTotalNumberOfModifierItems' &&
      name !== 'maxTotalNumberOfModifierItems' &&
      name !== 'minAnySingleModifierItems' &&
      name !== 'maxAnySingleModifierItems' &&
      name !== 'free'
    ) {
      copyFormErrors = set(formErrors, name, {
        error: true,
        message: t('field_is_required_'),
      });
      updateFormErrors({ ...copyFormErrors });
    } else {
      copyFormErrors = set(formErrors, name, { error: false, message: '' });
      if (formState?.products && formState.list) {
        formState.list.forEach((listItem) => {
          formState.products.map((item) => {
            if (currentProduct?._id === item._id) {
              if (
                currentProduct?.connectedProviders[providers.ubereats.srv] &&
                priceTransform(value) > 374 &&
                !menu?.disablePriceLimitCheck
              ) {
                copyFormErrors = set(formErrors, name, {
                  error: true,
                  message: t('price_$375.00_for_ubereats_'),
                });
              } else {
                copyFormErrors = set(formErrors, name, {
                  error: false,
                  message: '',
                });
              }
            }
          });
        });
      } else {
        if (formState.list && !isEdit) {
          formState.list.forEach((listItem) => {
            if (
              currentProduct?.connectedProviders[providers.ubereats.srv] &&
              priceTransform(value) > 374 &&
              !menu?.disablePriceLimitCheck
            ) {
              copyFormErrors = set(formErrors, name, {
                error: true,
                message: t('price_$375.00_for_ubereats_'),
              });
            } else {
              copyFormErrors = set(formErrors, name, {
                error: false,
                message: '',
              });
            }
          });
        }
      }
    }

    const sizePriceMatch = name.match(SIZE_PRICE_PATH);
    let newData;
    if (sizePriceMatch) {
      const itemIndex = Number(sizePriceMatch[1]);
      const sizeIndex = Number(sizePriceMatch[2]);
      newData = {
        ...formState,
        list: (formState.list || []).map((item, index) => {
          if (index !== itemIndex) {
            return item;
          }
          return {
            ...item,
            priceOverride: (item.priceOverride || []).map(
              (override, overrideIndex) =>
                overrideIndex === sizeIndex
                  ? { ...override, price: value }
                  : override
            ),
          };
        }),
      };
    } else {
      newData = set(
        {
          ...formState,
          list: cloneModifierList(formState.list),
        },
        name,
        value
      );
    }
    if (setIsDirty) {
      setIsDirty(true);
    }
    if (setIsChanged) {
      setIsChanged(true);
    }
    setChanged?.(true);
    if (
      NAME_ENUMS.minQ === name ||
      NAME_ENUMS.maxQ === name ||
      NAME_ENUMS.min === name ||
      NAME_ENUMS.max === name ||
      NAME_ENUMS.minT === name ||
      NAME_ENUMS.maxT === name ||
      NAME_ENUMS.minAnySingle === name ||
      NAME_ENUMS.maxAnySingle === name ||
      NAME_ENUMS.free === name
    ) {
      if (validateNumber(value, name)) {
        // `newData` carries the keystroke the user just made; `formState` is
        // still the previous render's value and would validate one edit behind.
        validationModifierCustomersCan(
          newData,
          copyFormErrors,
          formErrors,
          updateFormErrors,
          name
        );
      } else {
        if (
          name === NAME_ENUMS.minAnySingle &&
          Number(value) === 0 &&
          value !== ''
        ) {
          copyFormErrors = set(formErrors, name, { error: false, message: `` });
        } else if (
          name === NAME_ENUMS.minAnySingle &&
          (value === '' || value === null || value === undefined)
        ) {
          copyFormErrors = set(formErrors, name, {
            error: true,
            message: `This field is required`,
          });
        }
        if (name === NAME_ENUMS.maxAnySingle && !value) {
          copyFormErrors = set(formErrors, name, {
            error: true,
            message: `menu.min_number`,
          });
        }
        updateFormErrors({ ...copyFormErrors });
      }
    }
    newData?.list?.forEach((item) => {
      item.price = Number(item.price);
    });
    updateFormState({ ...newData });
  };

  const markModifierDirty = () => {
    if (setIsDirty) {
      setIsDirty(true);
    }
    if (setIsChanged) {
      setIsChanged(true);
    }
    if (setChanged) {
      setChanged(true);
    }
  };

  const handleChangeProviderField = (eventOrName, nextValue) => {
    const isEvent = typeof eventOrName !== 'string';
    const name = isEvent
      ? eventOrName.currentTarget?.name || eventOrName.target?.name
      : eventOrName;
    let value = isEvent
      ? eventOrName.currentTarget?.value ?? eventOrName.target?.value
      : nextValue;

    if (!name) {
      return;
    }

    if (name.includes('price') && typeof value !== 'boolean') {
      value = priceTransform(String(value ?? ''));
      value = parseFloat(Number(value).toFixed(2));
    }

    markModifierDirty();
    const newData = set(
      {
        ...formState,
        list: cloneModifierList(formState.list),
      },
      name,
      value
    );
    updateFormState({ ...newData });
  };

  const handleRestoreProviderOverrides = (
    itemIndex,
    providerIndex,
    overrides
  ) => {
    const formStateCopy = {
      ...formState,
      list: formState.list.map((listItem, index) => {
        if (index !== itemIndex) {
          return listItem;
        }
        if (providerIndex === 0 || providerIndex > 0) {
          return {
            ...listItem,
            priceOverride: (listItem.priceOverride || []).map(
              (sizeOverride, sizeIndex) =>
                sizeIndex === providerIndex
                  ? { ...sizeOverride, priceOverride: overrides }
                  : sizeOverride
            ),
          };
        }
        return {
          ...listItem,
          priceOverride: overrides,
        };
      }),
    };
    markModifierDirty();
    updateFormState(formStateCopy);
  };

  const getOriginalListItem = (itemIndex) => {
    const current = formState.list[itemIndex];
    const originalList = originalListRef.current || [];
    if (current?._id) {
      return originalList.find((item) => item._id === current._id);
    }
    return null;
  };

  const getOriginalPrice = (itemIndex, sizeIndex) => {
    const originalItem = getOriginalListItem(itemIndex);
    if (!originalItem) {
      return undefined;
    }
    if (sizeIndex === 0 || sizeIndex > 0) {
      const currentSize = formState.list[itemIndex]?.priceOverride?.[sizeIndex];
      const originalSize =
        originalItem.priceOverride?.find(
          (item) => item.context_value === currentSize?.context_value
        ) || originalItem.priceOverride?.[sizeIndex];
      return originalSize?.price;
    }
    return originalItem.price;
  };

  const handleBlurPrice = (e, itemIndex, sizeIndex) => {
    const { name, value } = e.target;
    const isSizePrice = sizeIndex === 0 || sizeIndex > 0;
    const listItem = formState.list[itemIndex];
    const oldPrice = getOriginalPrice(itemIndex, sizeIndex);
    const currentPrice =
      priceTransform(String(value ?? '')) ??
      (isSizePrice
        ? listItem?.priceOverride?.[sizeIndex]?.price
        : listItem?.price);
    const overrides = isSizePrice
      ? listItem?.priceOverride?.[sizeIndex]?.priceOverride
      : listItem?.priceOverride;
    const priceChanged =
      (oldPrice || oldPrice === 0) &&
      toComparablePrice(oldPrice) !== toComparablePrice(currentPrice);
    const checkProvider = isEdit && hasProviderCustomizations(overrides);

    if (priceChanged && checkProvider) {
      dispatch({
        type: SET_CHANGE_PRICE_V2,
        payload: {
          changeBasePrice: true,
          title: 'changeBasePriseDialog.title',
          msg: 'changeBasePriseDialog.desc',
          msg2: 'changeBasePriseDialog.desc2',
          price: currentPrice,
          oldPrice,
          itemIndex,
          sizeIndex,
          name,
          isConfirm: false,
        },
      });
      return;
    }

    dispatch({
      type: SET_CHANGE_PRICE_V2,
      payload: {
        changeBasePrice: false,
        title: '',
        msg: '',
        msg2: '',
        price: oldPrice,
        isConfirm: false,
      },
    });
  };

  const handleKeyPressPrice = (e, itemIndex, sizeIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleBlurPrice(e, itemIndex, sizeIndex);
    }
  };

  useEffect(() => {
    if (
      !changeBasePriceData?.isConfirm ||
      changeBasePriceData?.price === undefined ||
      !changeBasePriceData?.name?.startsWith('list[')
    ) {
      return;
    }

    const sizeMatch = changeBasePriceData.name.match(
      /^list\[(\d+)\]\.priceOverride\[(\d+)\]\.price$/
    );
    const baseMatch = changeBasePriceData.name.match(/^list\[(\d+)\]\.price$/);
    const itemIndex =
      changeBasePriceData.itemIndex ??
      Number(sizeMatch?.[1] ?? baseMatch?.[1]);
    const sizeIndex = sizeMatch
      ? Number(sizeMatch[2])
      : changeBasePriceData.sizeIndex;
    const isSizePrice = sizeIndex === 0 || sizeIndex > 0;

    const formStateCopy = {
      ...formState,
      list: formState.list.map((listItem, index) => {
        if (index !== itemIndex) {
          return listItem;
        }
        if (isSizePrice) {
          return {
            ...listItem,
            priceOverride: (listItem.priceOverride || []).map(
              (sizeOverride, sizeOverrideIndex) =>
                sizeOverrideIndex === sizeIndex
                  ? {
                      ...sizeOverride,
                      priceOverride: lockProviderOverrides(
                        sizeOverride.priceOverride
                      ),
                    }
                  : sizeOverride
            ),
          };
        }
        return {
          ...listItem,
          priceOverride: lockProviderOverrides(listItem.priceOverride),
        };
      }),
    };

    const originalList = originalListRef.current || [];
    const currentItem = formStateCopy.list[itemIndex];
    originalListRef.current = originalList.map((item) => {
      if (currentItem?._id && item._id === currentItem._id) {
        return JSON.parse(JSON.stringify(currentItem));
      }
      return item;
    });

    markModifierDirty();
    updateFormState(formStateCopy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    changeBasePriceData?.isConfirm,
    changeBasePriceData?.price,
    changeBasePriceData?.name,
    changeBasePriceData?.itemIndex,
    changeBasePriceData?.sizeIndex,
  ]);

  const handleKeyPress = (e) => {
    const keyCode = e.keyCode || e.which;
    const allowedKeyCodes = [46, 8, 9, 27, 13, 110, 107, 109];
    if (
      allowedKeyCodes.indexOf(keyCode) === -1 &&
      (e.shiftKey || keyCode < 48 || keyCode > 57)
    ) {
      e.preventDefault();
    }
  };

  return (
    <>
      {loading ? (
        <Preloader />
      ) : (
        <Box>
          {mode === 'edit' && isInOverview && products?.length ? (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ py: 1, px: 2, background: '#DCE8FF', mb: 3, mt: 2 }}
            >
              <InfoOutlinedIcon />

              <Typography sx={{ ml: 2 }}>
                {t('menu.this_group_attached_applied_everywhere')}
              </Typography>
            </Stack>
          ) : null}
          <Grid container xl={isInOverview ? 12 : 6} flexDirection="column">
            <Grid item xl={isInOverview ? 8 : 5}>
              <Box
                onClick={(event) =>
                  handleClickPopover(event, 'name', 'batch_report.name', name)
                }
              >
                <TextField
                  onChange={handleChangeField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={name}
                  size="small"
                  sx={{ width: '100%', mt: 2 }}
                  label={t('Name')}
                  name="name"
                  error={formErrors?.name?.error}
                  helperText={t(formErrors?.name?.message)}
                  InputProps={{
                    endAdornment: isEdit ? (
                      <LocalizationPopover
                        id={_id}
                        field="name"
                        label={currentSelectedLabel}
                        required={true}
                        values={currentSelectedValues}
                        open={open}
                        handleClose={handleClose}
                        languageLimit={languageLimit}
                        currentSelectedField={currentSelectedField}
                        handleChangeField={handleChangeField}
                        isModifier={true}
                      />
                    ) : null,
                    inputRef: inputRef,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xl={isInOverview ? 8 : 5}>
              <Box
                onClick={(event) =>
                  handleClickPopover(
                    event,
                    'title',
                    'settings.internal_name',
                    title
                  )
                }
              >
                <TextField
                  onChange={handleChangeField}
                  value={title}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ width: '100%', mt: 2 }}
                  label={t('settings.internal_name')}
                  name="title"
                  error={formErrors?.title?.error}
                  helperText={formErrors?.title?.message}
                  InputProps={{
                    endAdornment: isEdit ? (
                      <LocalizationPopover
                        id={_id}
                        field="title"
                        label={currentSelectedLabel}
                        required={true}
                        values={currentSelectedValues}
                        open={open}
                        handleClose={handleClose}
                        languageLimit={languageLimit}
                        currentSelectedField={currentSelectedField}
                        handleChangeField={handleChangeField}
                        isModifier={true}
                      />
                    ) : null,
                    inputRef: internalRef,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xl={isInOverview ? 8 : 12}>
              <Box
                onClick={(event) =>
                  handleClickPopover(
                    event,
                    'description',
                    'schedule.description',
                    description
                  )
                }
              >
                <TextField
                  onChange={handleChangeField}
                  size="small"
                  sx={{ width: '100%', mt: 2 }}
                  multiline={true}
                  name="description"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label={t('Description')}
                  type="textarea"
                  rows="3"
                  value={description}
                  InputProps={{
                    endAdornment: isEdit ? (
                      <LocalizationPopover
                        id={_id}
                        field="description"
                        label={currentSelectedLabel}
                        required={false}
                        values={currentSelectedValues}
                        open={open}
                        handleClose={handleClose}
                        languageLimit={languageLimit}
                        currentSelectedField={currentSelectedField}
                        handleChangeField={handleChangeField}
                        isModifier={true}
                      />
                    ) : null,
                    inputRef: textAreaRef,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid container alignItems="center" sx={{ mt: 3 }}>
            <Grid item xs={'auto'}>
              <Typography sx={{ color: '#3D4350', fontWeight: 500 }}>
                {t('menu.modifier_options_price')}
              </Typography>
            </Grid>
            <Grid item xs sx={{ ml: 3 }}>
              <Divider />
            </Grid>
          </Grid>
          <Stack direction="row" sx={{ my: 3 }}>
            {!isInModifierGroupPage && (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={currentProduct?.isOnePrice || mode === 'edit'}
                      onChange={handleChangeIsOnePrice}
                      checked={currentProduct?.isOnePrice ? false : !isOnePrice}
                      name="isOnePrice"
                    />
                  }
                  label={t('menu.modifier_prices_vary_size')}
                />
              </FormGroup>
            )}
          </Stack>
          <Stack sx={{ width: 'max-content', mb: 2 }}>
            <Button
              id="modifierFormAddOption"
              type="button"
              startIcon={<AddIcon />}
              sx={{ background: '#F2F2F2', color: '#333333' }}
              onClick={handleAddItem}
            >
              {t('menu.add_option')}
            </Button>
          </Stack>
          <Grid item xl={isInOverview ? 12 : 6}>
            <Box
              sx={{
                '& .sortable-helper': {
                  zIndex: 9999,
                  transform: 'scale(1.02) rotate(2deg)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                  borderRadius: '8px',
                  transition:
                    'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '& *': {
                    cursor: 'grabbing !important',
                  },
                },
              }}
            >
              <Suspense fallback={<Preloader />}>
                <SortableContainerList
                  handleSortEnd={handleSortEnd}
                  fields={list}
                  mode={mode}
                  isOnePrice={effectiveIsOnePrice}
                  connectedProviders={connectedProviders}
                  total={list?.length}
                  sizeTypeModifiers={sizeTypeModifiers}
                  onDelete={handleDeleteItem}
                  onAvailabilityChange={handleOptionAvailabilityChange}
                  isInOverview={isInOverview}
                  formState={formState}
                  handleChangeField={handleChangeField}
                  handleBlurPrice={handleBlurPrice}
                  handleKeyPressPrice={handleKeyPressPrice}
                  handleChangeProviderField={handleChangeProviderField}
                  onRestoreProviderOverrides={handleRestoreProviderOverrides}
                  formErrors={formErrors}
                  updateFormState={updateFormState}
                />
              </Suspense>
            </Box>
          </Grid>
          <Box sx={{ mt: 3 }} className="customers-can-section">
            <Box sx={{ mt: 3 }}>
              <Grid container alignItems="center">
                <Grid item xs={'auto'}>
                  <Typography sx={{ color: '#3D4350', fontWeight: 500 }}>
                    {t('menu.customers_can')}
                  </Typography>
                </Grid>
                <Grid item xs sx={{ ml: 3 }}>
                  <Divider />
                </Grid>
              </Grid>
              <>
                <Stack direction="row" sx={{ mt: 2, mb: 1, height: 65 }}>
                  <TextField
                    sx={{ width: '200px' }}
                    label={t('menu.select_least')}
                    name={`min`}
                    size="small"
                    onWheel={onWheelPreventChangeNumberField}
                    value={min}
                    InputLabelProps={labelProps}
                    onChange={handleChangeField}
                    onKeyPress={handleKeyPress}
                    error={formErrors?.min?.error}
                    helperText={
                      formErrors?.min?.error
                        ? !Number(min)
                          ? `${t('menu.min_number')}`
                          : Number(min) > 100000
                          ? t('menu.max_number')
                          : `${t(formErrors?.min?.message?.split(' ')[0])} ${
                              formErrors?.min?.message?.split(' ')[1]
                            }`
                        : ''
                    }
                  />
                  <TextField
                    sx={{ ml: 1, width: '200px' }}
                    label={t('menu.select_most')}
                    name={`max`}
                    onWheel={onWheelPreventChangeNumberField}
                    size="small"
                    value={max}
                    InputLabelProps={labelProps}
                    onChange={handleChangeField}
                    onKeyPress={handleKeyPress}
                    error={formErrors?.max?.error}
                    helperText={
                      formErrors?.max?.error
                        ? !Number(max)
                          ? `${t('menu.min_number')}`
                          : Number(max) > 100000
                          ? t('menu.max_number')
                          : `${t(formErrors?.max?.message?.split(' ')[0])} ${
                              formErrors?.max?.message?.split(' ')[1]
                            }`
                        : ''
                    }
                  />
                  <TextField
                    sx={{ ml: 1, width: '200px' }}
                    label={t('menu.free_options')}
                    onWheel={onWheelPreventChangeNumberField}
                    name={`free`}
                    size="small"
                    value={free}
                    InputLabelProps={labelProps}
                    onChange={handleChangeField}
                    onKeyPress={handleKeyPress}
                    error={formErrors?.free?.error}
                    helperText={
                      formErrors?.free?.error
                        ? !Number(free)
                          ? `${t('menu.min_number')}`
                          : Number(free) > 100000
                          ? t('menu.max_number')
                          : `${t(formErrors?.free?.message?.split(' ')[0])} ${
                              formErrors?.free?.message?.split(' ')[1]
                            }`
                        : ''
                    }
                  />

                  <Tooltip
                    title={t('menu.free_options_add_price')}
                    followCursor
                  >
                    <ErrorOutlineIcon sx={{ ml: 2 }} />
                  </Tooltip>
                </Stack>

                <MinAndMaxOptions
                  handleChangeMultipleOptions={handleChangeMultipleOptions}
                  canSelectMultipleOptions={canSelectMultipleOptions}
                  minAnySingleModifierItems={minAnySingleModifierItems}
                  labelProps={labelProps}
                  handleChangeField={handleChangeField}
                  formErrors={formErrors}
                  maxAnySingleModifierItems={maxAnySingleModifierItems}
                  minTotalNumberOfModifierItems={minTotalNumberOfModifierItems}
                  handleChangeFieldMinTotalNumberOfModifierItems={
                    handleChangeFieldMinTotalNumberOfModifierItems
                  }
                  maxTotalNumberOfModifierItems={maxTotalNumberOfModifierItems}
                  handleChangeFieldMaxTotalNumberOfModifierItems={
                    handleChangeFieldMaxTotalNumberOfModifierItems
                  }
                />
              </>
              <Box sx={{ mt: 3 }}>
                <Grid container alignItems="center">
                  <Grid item xs={'auto'}>
                    <Typography
                      sx={{
                        color: '#3D4350',
                        fontWeight: 500,
                      }}
                    >
                      {t('menu.connected_items_list')}
                    </Typography>
                  </Grid>
                  <Grid item xs sx={{ ml: 3 }}>
                    <Divider />
                  </Grid>
                </Grid>
                <Stack direction="row" sx={{ mt: 2, flexWrap: 'wrap' }}>
                  {products?.map((product) => (
                    <Box
                      key={product._id}
                      variant="contained"
                      sx={{
                        background: '#CFDFFF',
                        border: 'none',
                        color: 'black',
                        mr: 1,
                        mt: 2,
                        padding: '8px 12px',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {product.name}
                    </Box>
                  ))}
                </Stack>
              </Box>

              {isInOverview && (
                <Typography sx={{ fontSize: '13px', mt: 3 }}>
                  {t(
                    'menu.modifier_templates_are_going_editing_items_you_want'
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

// const SortableList = SortableContainer(({ children }) => <div>{children}</div>);

export default memo(ModifierGroupForm);
